import React, { useEffect, useMemo } from "react";
import {
  ITEMS_PER_PAGE,
  useNearbyHomeStays,
} from "@/lib/accommodation/plumGuide";
import { AccommodationListing } from "@/types";
import LoadingCard from "@tripbuilder/cat-accommodation/right/LoadingCard";
import AccommodationCard from "@tripbuilder/cat-accommodation/right/AccommodationCard";
import useAccommodationStore from "@/stores/tripBuilder/useAccommodation";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, BedDouble } from "lucide-react";
import useCurrencyStore from "@/stores/ui/currencyStore";
import useTrip from "@/stores/tripBuilder/useTrip";
import { DateTime } from "luxon";
import useAuthStore from "@/stores/user/authStore";
import { useRateLimitStore } from "@/stores/rateLimitStore";
import useTransport from "@/stores/tripBuilder/useTransport";
import { useToast } from "@/hooks/use-toast";
import logger from "@/utils/logger";

interface HomeStaysProps {
  handleAddToTrip: (listing: AccommodationListing) => void;
  handleModalOpenMainLodging: (viewLodging: AccommodationListing) => void;
  isAdmin: boolean;
  closeModal?: () => void;
}

const HomeStays: React.FC<HomeStaysProps> = ({
  handleAddToTrip,
  handleModalOpenMainLodging,
  isAdmin,
  closeModal,
}) => {
  const accommodationBounds = useTransport(
    (state) => state.accommodationBounds
  );
  const { selectedCurrency } = useCurrencyStore();
  const { anonymousUser } = useAuthStore();
  const { setRateLimit } = useRateLimitStore();
  const tripData = useTrip((state) => state.tripData);
  const selectedLocation = useTrip((state) => state.selectedLocation);
  const { getEffectiveArrivalDate } = useTransport();
  const {
    shouldSearchArea,
    setShouldSearchArea,
    setResponseHomeStays,
    setIsFetchingHomeStays,
    currentPage,
    setCurrentPage,
    selectedPriceRange,
  } = useAccommodationStore();
  const { toast } = useToast();

  const numNights = useMemo(() => {
    if (!selectedLocation || !tripData) return 1;

    const effectiveArrival = getEffectiveArrivalDate(
      tripData.transport,
      selectedLocation.id
    );
    const arrivalDate = effectiveArrival
      ? DateTime.fromISO(effectiveArrival).setZone(
          selectedLocation.date.time_zone
        )
      : DateTime.fromISO(selectedLocation.date.start_date).setZone(
          selectedLocation.date.time_zone
        );

    const checkOutDate = DateTime.fromISO(
      selectedLocation.date.end_date
    ).setZone(selectedLocation.date.time_zone);

    return Math.ceil(checkOutDate.diff(arrivalDate, "days").days);
  }, [selectedLocation, tripData, getEffectiveArrivalDate]);

  const { data, error, isFetching, status, refetch } = useNearbyHomeStays(
    accommodationBounds,
    currentPage,
    selectedCurrency.code,
    selectedPriceRange,
    numNights
  );

  useEffect(() => {
    setIsFetchingHomeStays(isFetching);
  }, [isFetching, setIsFetchingHomeStays]);

  // useEffect(() => {
  //   if (shouldSearchArea) {
  //     refetch();
  //     setShouldSearchArea(false);
  //   }
  // }, [shouldSearchArea, refetch, setShouldSearchArea]);

  useEffect(() => {
    if (data) {
      setResponseHomeStays(data.data);
    }
  }, [data, setResponseHomeStays]);

  // when plum guide doesn't give us a response
  useEffect(() => {
    if (error) {
      logger.log("Plum guide error", error);
      const statusCode = (error as any)?.response?.status;
      if (statusCode === 500) {
        if (closeModal) {
          closeModal();
        }
        toast({
          title: "Error",
          description: "Please try again later",
          variant: "default",
        });
      }
    }
  }, [error, toast, closeModal]);

  const handleNextPage = () => {
    if (anonymousUser) {
      setRateLimit("Create your account to view more lodging options!");
      return;
    }
    if (data?.nextCursor !== null && data?.nextCursor !== undefined) {
      setCurrentPage(data.nextCursor);
    }
  };

  const handlePrevPage = () => {
    if (currentPage >= ITEMS_PER_PAGE) {
      setCurrentPage(currentPage - ITEMS_PER_PAGE);
    }
  };

  return (
    <div className="flex flex-col w-full min-h-full">
      <div className="home-stays-list w-full mt-2 rounded-lg overflow-y-scroll no-scrollbar h-[calc(100vh-140px)]">
        {status === "pending" || isFetching ? (
          <div className="w-full grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3">
            {Array.from({ length: 8 }, (_, i) => i).map((_, index) => (
              <LoadingCard key={index} />
            ))}
          </div>
        ) : data?.data.length === 0 ? (
          <div className="flex flex-col min-h-full items-center justify-center p-8 text-center bg-neutral-50 rounded-md">
            <BedDouble className="w-8 h-8 text-neutral-300 mb-4" />
            <p className="text-lg text-neutral-600">No home stays found</p>
            <p className="text-sm font-light text-neutral-400 mt-1">
              Try expanding your search radius
            </p>
          </div>
        ) : (
          <div className="w-full grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3">
            {data?.data.map((listing: AccommodationListing) => (
              <AccommodationCard
                key={listing.listingid}
                listing={listing}
                handleAddToTrip={handleAddToTrip}
                handleModalOpenMainLodging={handleModalOpenMainLodging}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        )}
        {data?.data && data.data.length > 0 && (
          <div className="flex justify-center items-center gap-4 py-4 mt-2 bg-white border-t">
            <Button
              variant="outline"
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              className="flex items-center justify-center gap-1.5 rounded-full font-light text-sm px-3 py-1.5 hover:bg-neutral-100 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm text-neutral-500 font-light px-2">
              Page {Math.floor(currentPage / ITEMS_PER_PAGE) + 1}
            </span>
            <Button
              variant="outline"
              onClick={handleNextPage}
              disabled={data?.nextCursor === null}
              className="flex items-center justify-center gap-1.5 rounded-full font-light text-sm px-3 py-1.5 hover:bg-neutral-100 transition-colors"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeStays;
